/* eslint-disable jsx-a11y/no-autofocus */
import styled from 'styled-components';

import { Alert, Collapse, IconButton, Stack, TextField, ToggleButton, ToggleButtonGroup, alpha } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { Command, tabActions } from '@store/slices';
import { Condition } from './Condition';
import { Button } from '@common/Button';
import { Tooltip } from '@common/Tooltip';
import { useTheme } from '../../../../styles';
import { NodeConditionGroup, NodeField } from '@components/Flow/Nodes';

import {
  AddCircleOutline,
  AddOutlined,
  CalculateOutlined,
  DeleteOutline,
  EditOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  PauseCircleOutline,
  SaveOutlined,
} from '@mui/icons-material';
import { State } from '@store/index';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

const CardContainer = styled.div<{ error?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid ${({ theme, error }) => (error ? theme.telnyx.error : theme.line.light)};
  white-space: pre-wrap;

  p {
    color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
    font-size: 14px;
    font-family: 'Inter';
    margin-bottom: 4px;
  }

  a {
    color: ${({ theme }) => theme.primary};
  }
`;

const CardHeader = styled.div<{ noBody?: boolean }>`
  display: flex;
  flex-direction: column;

  gap: 16px;
  padding: 16px;

  pre {
    color: ${({ theme }) => theme.text.standard};
    font-size: 14px;
    font-weight: bold;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 28px;
    max-height: 28px;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 11px;
    min-height: 30px;
    width: 100%;
    min-width: 0;

    pre {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      margin-right: 10px;
    }
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 25px;
    color: ${({ theme }) => theme.text.standard};
    max-width: 25px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
  gap: 1.25rem;
  margin-bottom: 2rem;
  color: ${({ theme }) => theme.text.standard};
`;

const CardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  border-bottom: 1px solid ${({ theme }) => theme.line.light};
  border-top: 1px solid ${({ theme }) => theme.line.light};
  padding: 16px;
  margin-bottom: 28px;

  & .MuiToggleButton-root {
    padding: 4px 12px;
  }
`;

const GroupDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    color: ${({ theme }) => theme.text.standard};
    margin-left: 5px;
    font-size: 18px;
  }

  p {
    color: ${({ theme }) => theme.text.subtext};
    font-size: 12px;
    font-weight: 500;
  }

  .divider {
    height: 0px;
    border-top: 1px solid ${({ theme }) => theme.line.light};
    width: 100%;
  }
`;

interface Props {
  field: NodeField;
  group: NodeConditionGroup;
  command: Command;
  disabled: boolean;
  editing: boolean;
  setEditing: React.Dispatch<string | null>;
  openGroups: string[];
  setOpenGroups: React.Dispatch<string[]>;
}

export const ConditionGroup: React.FC<Props> = ({
  field,
  group,
  command,
  disabled,
  editing,
  openGroups,
  setOpenGroups,
  setEditing,
}) => {
  const dispatch = useDispatch();
  const collapsed = openGroups.includes(group.id);
  const theme = useTheme();

  const { workflow_id } = useParams();

  const allNodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors);

  const { id, name, operator = 'and', conditions } = group;

  const toggleCollapse = () => {
    if (openGroups.includes(group.id)) setOpenGroups(openGroups.filter((item) => item !== group.id));
    else setOpenGroups(openGroups.concat(group.id));
  };

  const addConditionToGroup = () => {
    const fieldValue = {
      id: uuidv4(),
      input: '',
      operator: 'equals',
      value: '',
    };

    dispatch(
      tabActions.nodeInputArrayNestedArrayPush({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldId: group.id,
        nestedFieldName: 'conditions',
        value: fieldValue,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const updateConditionGroupTitle = (value: string) => {
    dispatch(
      tabActions.nodeInputArrayUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldId: group.id,
        fieldName: 'name',
        value,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const removeConditionGroup = (nestedFieldId: string) => {
    dispatch(tabActions.removeEdgeByHandle({ node_id: command.node.id, handle: nestedFieldId }));

    dispatch(
      tabActions.nodeInputArrayFilter({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldId,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const updateGroupOperator = (value: string) => {
    if (!value) return;

    dispatch(
      tabActions.nodeInputArrayUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldId: group.id,
        fieldName: 'operator',
        value,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const toggleEditState = () => {
    if (editing) setEditing(null);
    else {
      setEditing(id);
      setOpenGroups([id]);
    }
  };

  const nodeErrors = allNodeErrors.filter((nodeError) => nodeError.id === command.node.id);

  // condition input field error
  const conditionGroupError = nodeErrors?.find(
    (nodeError) =>
      nodeError.id === command.node.id &&
      nodeError?.workflowId === workflow_id &&
      nodeError?.fields?.find((nodeField) => nodeField.id === group.id)
  );

  const conditionGroupErrorField = conditionGroupError
    ? conditionGroupError?.fields?.find((nodeField) => nodeField.id === group.id)
    : null;

  const conditionGroupErrorMessage = conditionGroupErrorField ? conditionGroupErrorField.message : undefined;
  const hasErrors = Boolean(nodeErrors.length > 0);
  const hasError = Boolean(conditionGroupErrorMessage);

  useEffect(() => {
    if (conditionGroupError && !collapsed) {
      setOpenGroups([...openGroups, id]);
      setEditing(null);
    }
  }, [conditionGroupErrorMessage]);

  return (
    <CardContainer error={hasError}>
      <CardHeader>
        <div className='row'>
          <div className='title'>
            <div className='icon'>
              <CalculateOutlined />
            </div>
            {editing ? (
              <TextField
                value={name}
                autoFocus
                variant='standard'
                error={name.length === 0}
                onChange={(e) => updateConditionGroupTitle(e.target.value)}
                fullWidth
              />
            ) : (
              <pre title={name}>{name}</pre>
            )}
          </div>
          <Stack direction='row' spacing={1} alignItems='center'>
            {!editing && (
              <Tooltip title='Delete Group' placement='top'>
                <IconButton
                  disabled={disabled}
                  onClick={() => removeConditionGroup(id)}
                  color='secondary'
                  sx={{ borderRadius: '6px' }}
                  size='small'
                >
                  <DeleteOutline sx={{ fontSize: '2ch !important' }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={editing ? 'Stop Editing' : 'Edit Conditions'} placement='top'>
              <IconButton
                disabled={hasErrors || disabled || (editing && name.length === 0)}
                onClick={toggleEditState}
                color={editing && name.length > 0 ? 'primary' : 'secondary'}
                sx={{ borderRadius: '6px' }}
                size='small'
              >
                {editing ? (
                  <SaveOutlined
                    sx={{ fontSize: '2ch !important', color: name.length > 0 ? theme.primary : undefined }}
                  />
                ) : (
                  <EditOutlined sx={{ fontSize: '2ch !important' }} />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={collapsed && !hasErrors ? 'Hide' : 'Show'} placement='top'>
              <IconButton
                size='small'
                disabled={disabled || hasErrors}
                sx={{ padding: '2px', borderRadius: '6px' }}
                onClick={toggleCollapse}
              >
                {collapsed && !hasErrors ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
              </IconButton>
            </Tooltip>
          </Stack>
        </div>
      </CardHeader>
      {hasError && (
        <Alert sx={{ margin: 2, marginTop: 0 }} severity='error'>
          {conditionGroupErrorMessage}
        </Alert>
      )}
      <Collapse in={hasErrors || (!disabled && (collapsed || editing))} timeout='auto' unmountOnExit>
        <div>
          <Collapse in={editing} timeout='auto' unmountOnExit>
            <CardActions>
              <Stack direction='row' justifyContent='space-between' width='100%'>
                <Button startIcon={<AddOutlined sx={{ color: theme.primary }} />} onClick={addConditionToGroup}>
                  Condition
                </Button>
                <ToggleButtonGroup
                  color='primary'
                  value={conditions.length <= 1 ? undefined : operator}
                  disabled={conditions.length <= 1}
                  exclusive
                  onChange={(_, newVal: 'and' | 'or') => updateGroupOperator(newVal)}
                  aria-label='text alignment'
                >
                  <Tooltip title='All conditions must be true' placement='top'>
                    <ToggleButton value='and' sx={{ padding: '8px' }}>
                      AND
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip title='One condition must be true' placement='top'>
                    <ToggleButton value='or'>OR</ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </Stack>
            </CardActions>
          </Collapse>
          <CardContent>
            {conditions.map((condition, index) => (
              <>
                <Condition
                  id={condition.id}
                  key={condition.id}
                  groupId={group.id}
                  label='Condition'
                  fieldId={field.id}
                  description={field.description}
                  removedDisabled={conditions.length <= 1}
                  command={command}
                  condition={condition}
                  editing={editing}
                />
                {conditions.length - 1 === index ? null : (
                  <GroupDivider>
                    {operator === 'and' ? <AddCircleOutline /> : <PauseCircleOutline />}
                    <Stack width='100%' alignItems='center' direction='row' spacing={2}>
                      <div className='divider' />
                      <p>{operator.toUpperCase()}</p>
                      <div className='divider' />
                    </Stack>
                  </GroupDivider>
                )}
              </>
            ))}
          </CardContent>
        </div>
      </Collapse>
    </CardContainer>
  );
};
